import styled from 'styled-components';

interface AlertProps {
  variant?: 'success' | 'error' | 'warning' | 'info';
  size?: 'small' | 'medium' | 'large';
  dismissible?: boolean;
}

const getAlertColors = (variant: string) => {
  switch (variant) {
    case 'success':
      return {
        background: '#f0f9ff',
        border: '#10b981',
        text: '#065f46',
        icon: '#10b981'
      };
    case 'error':
      return {
        background: '#fef2f2',
        border: '#ef4444',
        text: '#991b1b',
        icon: '#ef4444'
      };
    case 'warning':
      return {
        background: '#fffbeb',
        border: '#f59e0b',
        text: '#92400e',
        icon: '#f59e0b'
      };
    case 'info':
    default:
      return {
        background: '#eff6ff',
        border: '#3b82f6',
        text: '#1e40af',
        icon: '#3b82f6'
      };
  }
};

export const AlertContainer = styled.div<AlertProps>`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  padding: ${props => {
    switch (props.size) {
      case 'small': return '0.75rem';
      case 'large': return '1.25rem';
      default: return '1rem';
    }
  }};
  border-radius: 8px;
  border-left: 4px solid ${props => getAlertColors(props.variant || 'info').border};
  background: ${props => getAlertColors(props.variant || 'info').background};
  color: ${props => getAlertColors(props.variant || 'info').text};
  margin-bottom: 1rem;
  position: relative;
`;

export const AlertIcon = styled.div<AlertProps>`
  color: ${props => getAlertColors(props.variant || 'info').icon};
  font-size: ${props => {
    switch (props.size) {
      case 'small': return '1rem';
      case 'large': return '1.5rem';
      default: return '1.25rem';
    }
  }};
  flex-shrink: 0;
  margin-top: 0.125rem;
`;

export const AlertContent = styled.div`
  flex: 1;
`;

export const AlertTitle = styled.h4<AlertProps>`
  font-size: ${props => {
    switch (props.size) {
      case 'small': return '0.875rem';
      case 'large': return '1.125rem';
      default: return '1rem';
    }
  }};
  font-weight: 600;
  margin: 0 0 0.25rem 0;
`;

export const AlertMessage = styled.p<AlertProps>`
  font-size: ${props => {
    switch (props.size) {
      case 'small': return '0.75rem';
      case 'large': return '1rem';
      default: return '0.875rem';
    }
  }};
  margin: 0;
  line-height: 1.5;
`;

export const AlertCloseButton = styled.button<AlertProps>`
  background: none;
  border: none;
  color: ${props => getAlertColors(props.variant || 'info').text};
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 4px;
  font-size: 1.25rem;
  line-height: 1;
  opacity: 0.7;
  transition: opacity 0.2s ease;
  flex-shrink: 0;

  &:hover {
    opacity: 1;
  }

  &:focus {
    outline: none;
    opacity: 1;
  }
`;

// Specialized alert variants
export const SuccessAlert = styled(AlertContainer).attrs({ variant: 'success' })``;
export const ErrorAlert = styled(AlertContainer).attrs({ variant: 'error' })``;
export const WarningAlert = styled(AlertContainer).attrs({ variant: 'warning' })``;
export const InfoAlert = styled(AlertContainer).attrs({ variant: 'info' })``;

export const InlineAlert = styled(AlertContainer)`
  margin-bottom: 0.5rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
`;